import { useEffect, useMemo, useState } from 'react';
import { deleteDoc, doc, updateDoc, increment } from 'firebase/firestore';
import { db } from '../../../firebase';
import { Trash2 } from 'lucide-react';
import { Participant } from '../../../interfaces/reservation.interface';
import { fetchParticipants } from '../../../utils/reservation';

interface Props {
  workshopId: string;
  onClose: () => void;
}

const ParticipantsPopup = ({ workshopId, onClose }: Props) => {
  const [loading, setLoading] = useState(true);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Participant | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    let mounted = true;
    const run = async () => {
      try {
        // Déjà normalisées et triées par nom pour une lecture plus simple.
        const rows = await fetchParticipants(workshopId);
        if (mounted) setParticipants(rows);
      } catch (e: any) {
        console.error('Erreur chargement participants', e);
        if (mounted) setError('Impossible de charger les participants pour cet atelier.');
      } finally {
        if (mounted) setLoading(false);
      }
    };
    run();
    return () => {
      mounted = false;
    };
  }, [workshopId]);

  const handleDelete = async (participant: Participant) => {
    setDeleting(true);
    try {
      // 1. Supprimer la réservation
      await deleteDoc(doc(db, 'reservations', participant.id));

      // 2. Réincrémenter les places disponibles
      const workshopRef = doc(db, 'workshops_v2', workshopId);
      await updateDoc(workshopRef, {
        remainingPlaces: increment(participant.places),
      });

      // 3. Mettre à jour la liste locale
      setParticipants((prev) => prev.filter((p) => p.id !== participant.id));
      setConfirmDelete(null);
    } catch (err) {
      console.error('Erreur suppression réservation:', err);
      setError('Erreur lors de la suppression de la réservation.');
    } finally {
      setDeleting(false);
    }
  };

  const empty = useMemo(() => !loading && participants.length === 0 && !error, [loading, participants, error]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-3xl rounded-2xl bg-white shadow-xl border border-[#623413]/10">
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200">
          <h2 className="text-lg font-semibold text-[#623413]">Participants</h2>
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-sm rounded bg-neutral-100 hover:bg-neutral-200 text-neutral-700"
          >
            Fermer
          </button>
        </div>

        <div className="px-6 py-5">
          {loading && <p className="text-sm text-neutral-600">Chargement…</p>}
          {error && (
            <p className="text-sm text-red-600">
              {String(error).includes('Missing or insufficient permissions')
                ? 'Accès refusé. Vérifiez vos règles Firestore / rôle admin.'
                : error}
            </p>
          )}
          {empty && <p className="text-sm text-neutral-600">Aucune réservation pour cet atelier.</p>}

          {!loading && !error && participants.length > 0 && (
            <div className="overflow-x-auto">
              <table className="min-w-full border-collapse">
                <thead>
                  <tr className="text-left text-sm text-neutral-600 border-b">
                    <th className="py-2 pr-4">Nom</th>
                    <th className="py-2 pr-4">Email</th>
                    <th className="py-2 pr-4">Téléphone</th>
                    <th className="py-2 pr-4">Type</th>
                    <th className="py-2 pr-0 w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {participants.map((p) => (
                    <tr key={p.id} className="text-sm border-b last:border-b-0">
                      <td className="py-2 pr-4">{p.displayName || '—'}</td>
                      <td className="py-2 pr-4 break-all">{p.email || '—'}</td>
                      <td className="py-2 pr-4">{p.phone || '—'}</td>
                      <td className="py-2 pr-4">
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          p.type === 'duo'
                            ? 'bg-purple-100 text-purple-700'
                            : 'bg-blue-100 text-blue-700'
                        }`}>
                          {p.type === 'duo' ? 'Duo' : 'Solo'}
                        </span>
                      </td>
                      <td className="py-2 pr-0">
                        <button
                          onClick={() => setConfirmDelete(p)}
                          className="p-1.5 rounded hover:bg-red-50 text-neutral-400 hover:text-red-500 transition"
                          title="Supprimer la réservation"
                        >
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal de confirmation de suppression */}
      {confirmDelete && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-sm rounded-xl bg-white shadow-xl p-6">
            <h3 className="text-lg font-semibold text-[#623413] mb-2">
              Confirmer la suppression
            </h3>
            <p className="text-sm text-neutral-600 mb-4">
              Voulez-vous vraiment supprimer la réservation de{' '}
              <strong>{confirmDelete.displayName}</strong> ?
              {confirmDelete.places > 1 && (
                <span className="block mt-1 text-purple-600">
                  ({confirmDelete.places} places seront libérées)
                </span>
              )}
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setConfirmDelete(null)}
                disabled={deleting}
                className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition disabled:opacity-50"
              >
                Annuler
              </button>
              <button
                onClick={() => handleDelete(confirmDelete)}
                disabled={deleting}
                className="px-4 py-2 text-sm rounded-lg bg-red-500 text-white hover:bg-red-600 transition disabled:opacity-50"
              >
                {deleting ? 'Suppression...' : 'Supprimer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ParticipantsPopup;
